<script setup>
import { ref, onMounted, computed, onUnmounted, nextTick } from "vue";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { formatTime, formatDate } from "@/utils/functions";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";
import BaseTooltip from "@/components/ui/BaseTooltip.vue";

const storeRequests = useRequestsStore();
const storeSettings = useSettingsStore();

const receipts = ref(null);

const statusReceipt = {
	arrived: { title: "На проверке", icon: "arrived" },
	approved: { title: "Одобрен", icon: "approved" },
	rejected: { title: "Отклонен", icon: "rejected" },
	won: { title: "Выиграл", icon: "won" },
};

const statusReceipts = (status) => {
	return statusReceipt[status];
};

const overflowAuto = computed(() => (storeRequests.userReceiptReults.length >= 5 ? "--overflow-auto" : ""));
const isMobile = computed(() => storeSettings.widthWindow <= 576);

const scrollContainer = ref(null);
const sentinel = ref(null);
let observer = null;

const setupObserver = async () => {
	await nextTick();

	if (!sentinel.value || !scrollContainer.value) return;

	observer = new IntersectionObserver(
		(entries) => {
			const entry = entries[0];
			if (entry.isIntersecting && !storeRequests.loading && !storeRequests.allLoaded) {
				storeRequests.userReceipts();
			}
		},
		{
			root: scrollContainer.value,
			rootMargin: "0px 0px 10px 0px",
			threshold: 0,
		}
	);

	observer.observe(sentinel.value);
};

const showMoreReceipts = async () => {	
	storeRequests.allLoaded = false;
	await storeRequests.userReceipts();
	console.log('fff:',);
};

onMounted(async () => {
	await storeRequests.userReceipts();
	setupObserver();
	const script = document.createElement("script");
	const scriptPath = "/libs/spotlight.bundle.js";
	script.src = scriptPath;
	receipts.value.append(script);
});

onUnmounted(() => {
	if (observer && sentinel.value) {
		observer.unobserve(sentinel.value);
		observer.disconnect();
	}
});
</script>
<template>
	<div class="user-receipts" ref="receipts">
		<h2 class="user-receipts__title title2">Загруженные чеки</h2>
		<div class="user-receipts__holder" v-if="storeRequests.userReceiptReults.length">
			<ul :class="['user-receipts__list list-scrollbar spotlight-group', , overflowAuto]" ref="scrollContainer">
				<li class="user-receipts__item" v-for="receipt of storeRequests.userReceiptReults" :key="receipt.id">
					<div class="user-receipts__image spotlight" :data-src="receipt.images[0]">
						<svg width="38" height="38"><use href="#eye_black"></use></svg>
						<img class="full-img" width="40" height="40" :src="receipt.images[0]" alt="изображение" />
					</div>

					<div class="user-receipts__body">
						<span class="user-receipts__number">{{ receipt.number }}</span>

						<div :class="['user-receipts__status', { '--rejected': receipt.status === 'rejected' }]">
							<span>{{ statusReceipts(receipt.status).title }}</span>
							<BaseTooltip v-if="receipt.status === 'rejected'" :placement="isMobile ? 'left' : 'right'" :content="receipt.reject_reason">
								<svg width="24" height="24"><use href="#info"></use></svg>
							</BaseTooltip>
						</div>

						<div class="user-receipts__date">
							<span>{{ formatDate(receipt.upload_date) }}</span>
							<span>{{ formatTime(receipt.upload_date) }}</span>
						</div>
					</div>
				</li>

				<!-- Элемент, за которым мы наблюдаем -->
				<div ref="sentinel" class="sentinel" style="position: relative" v-if="!storeRequests.allLoaded">
					<img class="form__loader" style="position: absolute; left: 50%; transform: translateX(-50%)" width="36" height="36" src="@/assets/img/icons/loader_black.svg" alt="изображение" />
				</div>
				<button @click="showMoreReceipts" v-if="storeRequests.nextList && isMobile" class="user-receipts__more-show btn --border-grey">Показать ещё</button>
			</ul>
			<BaseButtonUploadReceipt class="user-receipts__btn btn --blue">Загрузить чек</BaseButtonUploadReceipt>
		</div>
		<div class="profile-empty" v-else>
			<p>У вас пока нет призов, загружайте чеки и выигрывайте!</p>
			<BaseButtonUploadReceipt class="profile-empty__btn btn --blue">Загрузить чек</BaseButtonUploadReceipt>
		</div>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.user-receipts {
	display: grid;

	padding: 8rem 0;
	@media (max-width: vars.$x480) {
		padding: 4.8rem 0;
	}
}
.user-receipts__holder {
	max-width: 1224px;
	width: 100%;
	margin: 0 auto;
}

.user-receipts__title {
	@media (max-width: vars.$x480) {
		margin-bottom: 2.2rem;
	}
}

.user-receipts__list {
	width: 100%;
	display: grid;
	gap: 1.6rem;
	@media (max-width: vars.$x425) {
		gap: 1rem;
	}
	&.--overflow-auto {
		max-height: 51rem;
		overflow-x: hidden;
		padding-right: 2rem;
		@media (max-width: vars.$xx) {
			max-height: 100%;
			overflow-y: visible;
			padding-right: 0;
		}
	}
}
.user-receipts__item {
	position: relative;
	display: grid;
	grid-template-columns: 7.2rem 1fr;
	align-items: center;
	gap: 2.4rem;
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	border: 1px solid rgba(14, 14, 14, 0.16);
	border-radius: var(--border-radius);
	padding: 2.1rem 2.4rem;
	@media (max-width: vars.$xx) {
		grid-template-columns: 14rem 1fr;
		gap: 16px;
	}
	@media (max-width: vars.$x480) {
		grid-template-columns: 8rem 1fr;
		padding: 16px;
		font-size: 1.6rem;
	}
}

.user-receipts__image {
	position: relative;
	border: 1px solid rgba(14, 14, 14, 0.16);
	border-radius: 1.6rem;
	width: 100%;
	aspect-ratio: 1/1;
	overflow: hidden;
	cursor: pointer;
	@media (max-width: vars.$x480) {
		border-radius: 0.9rem;
		width: 8rem;
		height: 8rem;
	}
	svg {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 3.8rem;
		height: 3.8rem;
		stroke: transparent;
		opacity: 0;
		transition: var(--transition) ease-in;
	}
	@media (hover: hover) {
		&:hover {
			svg {
				opacity: 1;
			}
		}
	}
}

.user-receipts__body {
	display: grid;
	grid-template-columns: 19rem 1fr 1fr;
	gap: 1.2rem;
	@media (max-width: vars.$t) {
		grid-template-columns: 1fr 1fr 1fr;
	}
	@media (max-width: vars.$xx) {
		grid-template-columns: 1fr;
		gap: .3rem;
	}
}

.user-receipts__number {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	@media (max-width: vars.$x480) {
		// max-width: 70px;
	}
}
.user-receipts__status {
	width: fit-content;
	position: relative;
	display: flex;
	align-items: center;
	gap: 1rem;
	@media (max-width: vars.$x480) {
		
	}
}
.user-receipts__date {
	--gap: 14px;
	display: flex;
	align-items: center;
	justify-self: start;
	gap: var(--gap);
	@media (max-width: vars.$xx) {
	}
	@media (max-width: vars.$x480) {
	}
	@media (max-width: vars.$x425) {
	}
	span {
		color: rgba(14, 14, 14, 0.4);
		&:first-child {
			display: flex;
			align-items: center;
			gap: var(--gap);

			&::after {
				content: "";
				border-radius: 100%;
				background-color: rgba(14, 14, 14, 0.4);
				width: 0.4rem;
				height: 0.4rem;
			}
		}
		&:last-child {
		}
	}
}
.user-receipts__more-show {
	margin-top: 6rem;
	@media (max-width: vars.$x480) {
		width: 100%;
		margin-top: 3.6rem;
	}
}
.user-receipts__btn {
	margin: 2rem auto 0;
	display: flex;
	@media (max-width: vars.$x480) {
		width: 100%;
	}
}
.profile-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	p {
		font-weight: 400;
		font-size: 2.6rem;
		line-height: 110%;
		margin-bottom: 6.4rem;
		text-align: center;
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
			margin-bottom: 3.2rem;
		}
	}
}
.profile-empty__btn {
	@media (max-width: vars.$x480) {
		width: 100%;
	}
}
</style>
